import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { ArrowLeft } from 'lucide-react';

const SettingsLayout: React.FC = () => {
  document.title = `Setting | EngageHub IQ`; const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showWorkspaceList, setShowWorkspaceList] = useState(true);

  const menuItems: { id: string; label: string; path: string }[] = [
    { id: 'integrations', label: 'Integrations', path: '/setting/integrations' },
    { id: 'workingHour', label: 'Working Hour', path: '/setting/working-hour' },
    { id: 'general', label: 'General', path: '/setting/general' },
  ];    // Determine active item based on current path
  const getActiveItem = () => {
    const currentPath = location.pathname;
    const activeMenuItem = menuItems.find(item => currentPath.startsWith(item.path));
    return activeMenuItem?.id || '';
  };

  const getActiveItemLabel = () => {
    const currentPath = location.pathname;
    const activeMenuItem = menuItems.find(item => currentPath.startsWith(item.path));
    return activeMenuItem?.label || '';
  };

  const handleMenuItemClick = (id: string) => {
    const menuItem = menuItems.find(item => item.id === id);
    if (menuItem) {
      navigate(menuItem.path);
      // Hide workspace list and show detail view
      setShowWorkspaceList(false);
      // Close sidebar on mobile after navigation
      setIsSidebarOpen(false);
    }
  }; const handleBackToWorkspace = () => {
    setShowWorkspaceList(true);
    navigate('/setting');
  };

  // Handle route changes to show/hide workspace list
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/setting' || currentPath === '/setting/') {
      setShowWorkspaceList(true);
    } else {
      const hasActiveItem = menuItems.some(item => currentPath.startsWith(item.path));
      if (hasActiveItem) {
        setShowWorkspaceList(false);
      }
    }
  }, [location.pathname, menuItems]); return (
    <div className="flex w-full h-[calc(100vh-70px)] bg-white relative overflow-hidden">
      {/* Mobile/Tablet: Show either workspace list or detail view */}
      <div className="lg:hidden w-full min-w-0">
        {showWorkspaceList ? (
          <>
            {/* Mobile Menu Button */}
            <div className="flex items-center justify-between p-4 border-b">
              <h6 className="text-15 font-semibold">Settings</h6>
            </div>

            {/* Workspace List */}
            <div className="p-4 overflow-y-auto">
              <div className="space-y-2">
                {menuItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleMenuItemClick(item.id)}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-w-0"
                  >
                    <span className="font-medium text-gray-900 truncate">{item.label}</span>
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Detail View Header with Back Button */}
            <div className="flex items-center gap-3 p-4 border-b min-w-0">
              <button
                onClick={handleBackToWorkspace}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h6 className="text-lg font-semibold truncate">{getActiveItemLabel()}</h6>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-auto min-w-0">
              {getActiveItem() && <Outlet />}
            </div>
          </>
        )}
      </div>

      {/* Desktop: Traditional sidebar layout */}
      <div className="hidden lg:flex w-full min-w-0">
        <Sidebar
          activeItem={getActiveItem()}
          setActiveItem={handleMenuItemClick}
          menuItems={menuItems}
        />
        <div className="flex-1 overflow-auto min-w-0">
          {getActiveItem() && <Outlet />}
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
